import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Users, 
  Target, 
  Cog,
  ArrowLeft,
  FileText,
  Database,
  GitBranch,
  CheckCircle,
  Workflow,
  Eye
} from 'lucide-react';

export const CanonEngineDefinition: React.FC = () => {
  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/elevation/glossary" 
            className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Glossary
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-consulting-slate-blue-100 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-consulting-slate-blue-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-charcoal-900">Canon Engine</h1>
              <p className="text-charcoal-600 mt-1">Authoritative source of truth management system</p>
            </div>
          </div>
        </div>

        {/* What It Is */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-consulting-slate-blue-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-consulting-slate-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-charcoal-900">What It Is</h2>
          </div>
          <div className="prose max-w-none">
            <p className="text-lg text-charcoal-700 leading-relaxed">
              A system that maintains the authoritative source of truth for all Peak Priorities content, ensuring documentation integrity, currency, and version management.
            </p>
          </div>
        </section>

        {/* Who It's For */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-sage-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-sage-600" />
            </div>
            <h2 className="text-2xl font-bold text-charcoal-900">Who It's For</h2>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-sage-50 border-l-4 border-sage-500">
              <h3 className="font-semibold text-charcoal-900 mb-2">Content creators and maintainers</h3>
              <p className="text-charcoal-700">Who need a single source of truth for all documentation</p>
            </div>
            <div className="p-4 bg-sage-50 border-l-4 border-sage-500">
              <h3 className="font-semibold text-charcoal-900 mb-2">System administrators</h3>
              <p className="text-charcoal-700">Managing documentation consistency across the system</p>
            </div>
            <div className="p-4 bg-sage-50 border-l-4 border-sage-500">
              <h3 className="font-semibold text-charcoal-900 mb-2">Developers</h3>
              <p className="text-charcoal-700">Who need accurate, current documentation that reflects the actual system state</p>
            </div>
            <div className="p-4 bg-sage-50 border-l-4 border-sage-500">
              <h3 className="font-semibold text-charcoal-900 mb-2">Anyone responsible for keeping documentation aligned</h3>
              <p className="text-charcoal-700">With the system state and ensuring coherence across all content</p>
            </div>
          </div>
        </section>

        {/* Why It's Important */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[var(--mustard-dim)] flex items-center justify-center">
              <Target className="h-5 w-5 text-[var(--mustard)]" />
            </div>
            <h2 className="text-2xl font-bold text-charcoal-900">Why It's Important</h2>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)]">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-charcoal-900 mb-1">Maintains the authoritative source of truth</h3>
                  <p className="text-charcoal-700">For all content, ensuring there's a single, definitive version of every piece of documentation</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)]">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-charcoal-900 mb-1">Maintains integrity with terms and relationships</h3>
                  <p className="text-charcoal-700">Across the system, preventing inconsistencies and contradictions</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)]">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-charcoal-900 mb-1">Ensures documentation reflects the current state</h3>
                  <p className="text-charcoal-700">Of the system, keeping it aligned with actual implementation</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)]">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-charcoal-900 mb-1">Manages versions of system parts</h3>
                  <p className="text-charcoal-700">Tracking changes and preserving revision history</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)]">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-charcoal-900 mb-1">Keeps docs current and correct</h3>
                  <p className="text-charcoal-700">Addressing the core opportunity to maintain documentation quality and accuracy</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-consulting-slate-blue-100 flex items-center justify-center">
              <Cog className="h-5 w-5 text-consulting-slate-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-charcoal-900">How It Works</h2>
          </div>
          <div className="space-y-6">
            {/* Source Discovery & Synthesis */}
            <div className="p-6 bg-consulting-slate-blue-50 border-l-4 border-consulting-slate-blue-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-consulting-slate-blue-100 flex items-center justify-center flex-shrink-0">
                  <Database className="h-6 w-6 text-consulting-slate-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Source Discovery & Synthesis</h3>
                  <p className="text-charcoal-700 leading-relaxed">
                    Discovers markdown sources, classifies them, and synthesizes into canonical documentation. 
                    The system scans the repository for source files, categorizes them by type and intent, 
                    and intelligently merges them into authoritative sections.
                  </p>
                </div>
              </div>
            </div>

            {/* Term & Relationship Integrity */}
            <div className="p-6 bg-sage-50 border-l-4 border-sage-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-sage-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Term & Relationship Integrity</h3>
                  <p className="text-charcoal-700 leading-relaxed">
                    Validates content against glossary definitions, detects term usage inconsistencies, and identifies namespace collisions. 
                    The system ensures that terms are used correctly and consistently across all documentation, 
                    flagging potential conflicts or misalignments.
                  </p>
                </div>
              </div>
            </div>

            {/* State Reflection */}
            <div className="p-6 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[var(--mustard-dim)] flex items-center justify-center flex-shrink-0">
                  <Eye className="h-6 w-6 text-[var(--mustard)]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-charcoal-900 mb-2">State Reflection</h3>
                  <p className="text-charcoal-700 leading-relaxed">
                    Compares staging vs production, generates changelogs, and ensures documentation matches current system state. 
                    The system provides detailed diffs showing exactly what changed, when, and why, 
                    enabling informed decisions about promoting changes to production.
                  </p>
                </div>
              </div>
            </div>

            {/* Version Management */}
            <div className="p-6 bg-consulting-slate-blue-50 border-l-4 border-consulting-slate-blue-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-consulting-slate-blue-100 flex items-center justify-center flex-shrink-0">
                  <GitBranch className="h-6 w-6 text-consulting-slate-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Version Management</h3>
                  <p className="text-charcoal-700 leading-relaxed">
                    Tracks versions of system parts, preserves revision history, and only increments versions when content actually changes. 
                    The system maintains a complete audit trail of all modifications, ensuring full traceability 
                    and preventing accidental version inflation.
                  </p>
                </div>
              </div>
            </div>

            {/* Staging/Production Workflow */}
            <div className="p-6 bg-sage-50 border-l-4 border-sage-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <Workflow className="h-6 w-6 text-sage-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Staging/Production Workflow</h3>
                  <p className="text-charcoal-700 leading-relaxed">
                    Provides staging environment for review, requires explicit approval, and prevents accidental production writes. 
                    All changes go through a staging synthesis process where they can be reviewed, compared, and approved 
                    before being promoted to production, ensuring quality and preventing unintended modifications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Related</h3>
          <div className="flex flex-wrap gap-3">
            <Link 
              to="/elevation/canon-engine" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-50 text-consulting-slate-blue-700 hover:bg-consulting-slate-blue-100 transition-colors"
            >
              <Cog className="h-4 w-4" />
              Canon Engine Dashboard
            </Link>
            <Link 
              to="/elevation/glossary" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--surface)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <BookOpen className="h-4 w-4" />
              Back to Glossary
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};









